import api from "./axios";

export interface ClassSchooll {
    id: string;
    code: string;
    name: string;
    grade_level: string;
    major_id?: string;
    major?: {
        id: string;
        code: string;
        name: string;
        description?: string;
        is_active: boolean;
    } | null;
    capacity: number;
    is_active: boolean;
    current_students_count?: number;
    available_slots?: number;
    is_full?: boolean;
    homeroom_teacher?: {
        id: string;
        teacher_id: string;
        nip: string;
        user: {
            id: string;
            name: string;
            email: string;
        };
    };
    subjects_count?: number;
    assignments_count?: number;
    exams_count?: number;
    materials_count?: number;
    created_at: string;
    updated_at: string;
}

export interface CreateClassSchoollData {
    code: string;
    name: string;
    grade_level: string;
    major_id?: string | null;
    homeroom_teacher_id?: string | null;
    capacity?: number;
    is_active?: boolean;
}

export interface UpdateClassSchoollData {
    code?: string;
    name?: string;
    grade_level?: string;
    major_id?: string | null;
    homeroom_teacher_id?: string | null;
    capacity?: number;
    is_active?: boolean;
}

export interface ClassSchoollFilters {
    search?: string;
    grade_level?: string;
    major_id?: string;
    semester_id?: string;
    homeroom_teacher_id?: string;
    is_active?: boolean;
    with_available_slots?: boolean;
    page?: number;
    per_page?: number;
}

export interface ClassSchoollStatistics {
    total_classes: number;
    active_classes: number;
    inactive_classes: number;
    classes_with_available_slots: number;
    full_classes: number;
}

export interface ClassSchoollListResponse {
    success: boolean;
    message: string;
    data: {
        items: ClassSchooll[];
        meta: {
            current_page: number;
            last_page: number;
            per_page: number;
            total: number;
            from: number;
            to: number;
        };
    };
}

export interface ClassSchoollResponse {
    success: boolean;
    message: string;
    data: ClassSchooll;
}

export const classSchoollApi = {
    async getAll(
        filters: ClassSchoollFilters = {},
    ): Promise<ClassSchoollListResponse> {
        const response = await api.get("/v1/classes", { params: filters });
        return response.data;
    },

    async getById(id: string): Promise<ClassSchoollResponse> {
        const response = await api.get(`/v1/classes/${id}`);
        return response.data;
    },

    async create(data: CreateClassSchoollData): Promise<ClassSchoollResponse> {
        const response = await api.post("/v1/classes", data);
        return response.data;
    },

    async update(
        id: string,
        data: UpdateClassSchoollData,
    ): Promise<ClassSchoollResponse> {
        const response = await api.put(`/v1/classes/${id}`, data);
        return response.data;
    },

    async delete(id: string): Promise<void> {
        await api.delete(`/v1/classes/${id}`);
    },

    async getByGradeLevel(
        gradeLevel: string,
    ): Promise<{ success: boolean; message: string; data: ClassSchooll[] }> {
        const response = await api.get(`/v1/classes/grade-level/${gradeLevel}`);
        return response.data;
    },

    async getByMajor(
        majorId: string,
    ): Promise<{ success: boolean; message: string; data: ClassSchooll[] }> {
        const response = await api.get(`/v1/classes/major/${majorId}`);
        return response.data;
    },

    async getBySemester(
        semesterId: string,
    ): Promise<{ success: boolean; message: string; data: ClassSchooll[] }> {
        const response = await api.get(`/v1/classes/semester/${semesterId}`);
        return response.data;
    },

    async getByHomeroomTeacher(
        teacherId: string,
    ): Promise<{ success: boolean; message: string; data: ClassSchooll[] }> {
        const response = await api.get(
            `/v1/classes/homeroom-teacher/${teacherId}`,
        );
        return response.data;
    },

    async getActive(): Promise<{
        success: boolean;
        message: string;
        data: ClassSchooll[];
    }> {
        const response = await api.get("/v1/classes/active");
        return response.data;
    },

    async getWithAvailableSlots(): Promise<{
        success: boolean;
        message: string;
        data: ClassSchooll[];
    }> {
        const response = await api.get("/v1/classes/available-slots");
        return response.data;
    },

    async search(
        query: string,
    ): Promise<{ success: boolean; message: string; data: ClassSchooll[] }> {
        const response = await api.get("/v1/classes/search", {
            params: { q: query },
        });
        return response.data;
    },

    async assignHomeroomTeacher(
        classId: string,
        teacherId: string,
    ): Promise<ClassSchoollResponse> {
        const response = await api.post(
            `/v1/classes/${classId}/assign-homeroom-teacher`,
            {
                teacher_id: teacherId,
            },
        );
        return response.data;
    },

    async removeHomeroomTeacher(
        classId: string,
    ): Promise<ClassSchoollResponse> {
        const response = await api.delete(
            `/v1/classes/${classId}/remove-homeroom-teacher`,
        );
        return response.data;
    },

    async toggleActive(
        classId: string,
        isActive: boolean,
    ): Promise<ClassSchoollResponse> {
        const response = await api.post(
            `/v1/classes/${classId}/toggle-active`,
            {
                is_active: isActive,
            },
        );
        return response.data;
    },

    async getStatistics(): Promise<{
        success: boolean;
        message: string;
        data: ClassSchoollStatistics;
    }> {
        const response = await api.get("/v1/classes/statistics");
        return response.data;
    },
};

export default classSchoollApi;
